import { AlertTriangle, Briefcase, Calendar, ChevronDown, ChevronUp, CreditCard, FileText, GraduationCap } from 'lucide-react';
import { useState } from 'react';

const TermsAndConditions = () => {
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const termsData = [
    {
      id: 'course-duration',
      title: 'Course Duration & Validity',
      icon: <Calendar className="w-5 h-5 text-accent" />,
      terms: [
        'The duration of the course is 3 months validity.',
        'Once the timing of the batch is finalized, it will never be changed according to the candidates.'
      ]
    },
    {
      id: 'assessments',
      title: 'Assessments & Performance',
      icon: <GraduationCap className="w-5 h-5 text-accent" />,
      terms: [
        'Students are required to complete all weekly assessments. Unexcused absences from these assessments will be subject to appropriate action.',
        'In weekly assessment performance, students must obtain a minimum of 50 marks.',
        'Consistent performance below expectations in assessments may necessitate an extended timeframe for program completion.',
        'Any instance of academic dishonesty or malpractice during assessments will be subject to disciplinary action, up to and including termination from the program.',
        'Students will be certified as having completed the course only upon successful completion and submission of all required daily tasks, weekly assessments and projects.'
      ]
    },
    {
      id: 'payments',
      title: 'Payment Terms',
      icon: <CreditCard className="w-5 h-5 text-accent" />,
      terms: [
        'Following the initial payment, the remaining balance is due in full on the program\'s official start date. A 48-hour grace period is granted for any outstanding balance.',
        'The initial ₹10,000 is non-refundable under any circumstances. No refund or fee adjustments will be entertained once the training begins.',
        'If the student secures a job through Vista Tech\'s placement support, the remaining ₹10,000 must be paid within 10 working days of receiving the offer letter.',
        'Failure to pay the remaining amount after placement may result in certificate withholding and potential legal action.'
      ]
    },
    {
      id: 'communication',
      title: 'Communication & Updates',
      icon: <FileText className="w-5 h-5 text-accent" />,
      terms: [
        'All official updates and program information will be disseminated via email. Students are responsible for checking their email regularly.',
        'The management is not responsible for missed communications due to unchecked email accounts.',
        'Students are required to provide timely updates on the completion of their daily tasks, adhering to established deadlines.',
        'Students experiencing difficulty comprehending new concepts are required to notify the management within 48 hours of the concept\'s introduction.',
        'Consistent communication with the management is mandatory throughout the duration of each project, beginning with the project\'s start.'
      ]
    },
    {
      id: 'internship-placement',
      title: 'Internship & Placement',
      icon: <Briefcase className="w-5 h-5 text-accent" />,
      terms: [
        'Internship will be only scheduled depends upon your weekly assessment performance.',
        'Internship and Live Project enable to the candidates depends upon their academic performance.',
        'Attendance at all scheduled interviews is mandatory. Failure to attend will result in consequences as determined by the program.',
        'Placement support includes resume building, mock interviews, referrals, and interview scheduling.',
        'Upon fulfillment of the aforementioned terms and circumstances, the firm offers a 100% placement guarantee.'
      ]
    },
    {
      id: 'attendance-conduct',
      title: 'Attendance & Conduct',
      icon: <AlertTriangle className="w-5 h-5 text-accent" />,
      terms: [
        'Two consecutive days of leave are not permitted in this program. Any leave requests must be submitted with prior notice.',
        'Prior to the commencement of class, students are requested to arrange themselves comfortably, regardless of the class format (online or offline).',
        'For the duration of online classes, students are expected to remain present. Leaving the online classroom before the scheduled end time will result in an absence being recorded.',
        'Students are required to sign the daily work done sheet only upon demonstrating comprehension of the daily topics covered.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Please read these terms and conditions carefully before enrolling in our programs
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Vista Tech Branding */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-primary text-white px-4 py-2 rounded-lg font-bold text-lg">
              VISTA TECH
            </div>
          </div>
          <p className="text-center text-gray-600">
            These terms and conditions govern your participation in Vista Tech's educational programs and services.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {termsData.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  {section.icon}
                  <h2 className="text-xl font-semibold text-primary text-left">
                    {section.title}
                  </h2>
                </div>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections[section.id] && (
                <div className="px-6 pb-6">
                  <div className="border-t border-gray-100 pt-4">
                    <ul className="space-y-3">
                      {section.terms.map((term, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="bg-accent text-primary rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                            {index + 1}
                          </span>
                          <p className="text-gray-700 leading-relaxed">
                            {term}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6 mt-8">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-red-800 mb-2">Important Notice</h3>
              <p className="text-red-700 leading-relaxed">
                Violation of any of the aforementioned terms and conditions may result in the immediate 
                cancellation of your enrollment, at the discretion of the company, based on the severity 
                of the infraction and the explanation provided.
              </p>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default TermsAndConditions;